// SPDX-License-Identifier: BUSL-1.1

/**
 * Heartbeat history table (HB-07, NocoDB #4408).
 *
 * Surfaces the append-only ops_agent_heartbeat_events log added in HB-01.
 * Displays last N heartbeats DESC with the invocation source as a badge,
 * the agent's reported status, and a relative-time timestamp.
 *
 * Cursor pagination uses occurred_at; "Load older" advances the cursor.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { HeartbeatSourceBadge, PiDevAgentStatusBadge } from '@/components/badges';
import { isHeartbeatInvocationSource } from '@cli-ceo/core';
import type { HeartbeatInvocationSource } from '@cli-ceo/core';
import type { PiDevAgentStatus } from '@/types/api';

interface HeartbeatRow {
  id: string;
  agentId: string;
  occurredAt: string;
  source: string;
  status: string;
  payload: Record<string, unknown>;
  signature: string | null;
  signatureKid: string | null;
}

interface HeartbeatListResponse {
  events: HeartbeatRow[];
  nextCursor: string | null;
}

const PAGE_LIMIT = 50;
const VALID_AGENT_STATUSES: readonly PiDevAgentStatus[] = [
  'idle',
  'running',
  'paused',
  'terminated',
];

function isAgentStatus(v: string): v is PiDevAgentStatus {
  return (VALID_AGENT_STATUSES as readonly string[]).includes(v);
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function asSource(v: string): HeartbeatInvocationSource {
  return isHeartbeatInvocationSource(v) ? v : 'unknown';
}

export function HeartbeatHistoryTable({ agentId }: { agentId: string }) {
  const [cursor, setCursor] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['agents', agentId, 'heartbeats', cursor],
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('limit', String(PAGE_LIMIT));
      if (cursor) qs.set('before', cursor);
      return apiFetch<HeartbeatListResponse>(
        `/agents/${encodeURIComponent(agentId)}/heartbeats?${qs.toString()}`,
      );
    },
    enabled: !!agentId,
  });

  if (isLoading) {
    return (
      <div className="rounded border border-neutral-800 bg-neutral-900 p-4 text-sm text-neutral-400">
        Loading heartbeats…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded border border-red-900 bg-red-900/20 p-4 text-sm text-red-300">
        Failed to load heartbeats: {String(error)}
      </div>
    );
  }

  const events = data?.events ?? [];

  if (events.length === 0) {
    return (
      <div className="rounded border border-neutral-800 bg-neutral-900 p-4 text-sm text-neutral-500">
        No heartbeats recorded yet.
      </div>
    );
  }

  return (
    <div className="rounded border border-neutral-800 bg-neutral-900">
      <table className="w-full text-sm">
        <thead className="bg-neutral-950 text-xs uppercase tracking-wider text-neutral-500">
          <tr>
            <th className="px-4 py-2 text-left">When</th>
            <th className="px-4 py-2 text-left">Source</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-left">Signed</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800">
          {events.map((row) => (
            <tr key={row.id} className="hover:bg-neutral-800/50">
              <td className="px-4 py-2 text-neutral-300">
                <span title={row.occurredAt}>{relativeTime(row.occurredAt)}</span>
              </td>
              <td className="px-4 py-2">
                <HeartbeatSourceBadge source={asSource(row.source)} />
              </td>
              <td className="px-4 py-2">
                {isAgentStatus(row.status) ? (
                  <PiDevAgentStatusBadge status={row.status} />
                ) : (
                  <span className="font-mono text-xs text-neutral-400">{row.status}</span>
                )}
              </td>
              <td className="px-4 py-2">
                {row.signature ? (
                  <span className="font-mono text-xs text-green-400" title={`kid: ${row.signatureKid ?? 'unknown'}`}>
                    ✓
                  </span>
                ) : (
                  <span className="text-xs text-neutral-600">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between border-t border-neutral-800 px-4 py-2 text-xs text-neutral-500">
        <span>{events.length} heartbeats</span>
        {data?.nextCursor ? (
          <button
            type="button"
            onClick={() => setCursor(data.nextCursor)}
            className="rounded bg-neutral-800 px-2 py-1 text-neutral-200 hover:bg-neutral-700"
          >
            Load older
          </button>
        ) : (
          <span>End of history</span>
        )}
      </div>
    </div>
  );
}
